import "./companyLogin.css";

import { useEffect, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "axios";

import {
  ArrowForwardRounded,
  CheckCircleOutlineRounded,
  DarkModeOutlined,
  ErrorOutlineRounded,
  KeyboardCapslockRounded,
  LightModeOutlined,
  LockRounded,
  PersonRounded,
  PhoneRounded,
  ShieldOutlined,
  Visibility,
  VisibilityOff,
  WifiOffRounded,
} from "@mui/icons-material";

import socket from "../../../../socket-io.js";
import * as Action from "../../../../store/redux/client_reducer.js";
import { serverHost } from "../../../../http_common.js";
import { userHomePath } from "../../../../authorization/companyUserAccess.js";
import UniLogo from "../../home-page/logo/universe.webp";

// Only a login identifier and a visual preference are stored; never save passwords or session tokens.
const REMEMBER_KEY = "universe_client_remembered_identifier";
const THEME_KEY = "universe_client_login_theme";

// The owner API already looks up founder.email and founder.phone. Company users
// still authenticate with the username issued to them (e.g. @universe.erp).
const looksLikePhone = (identifier) => /^\+?[0-9\s().-]{7,24}$/.test(identifier || "") &&
  (identifier.match(/\d/g) || []).length >= 7;
const isValidPhone = (identifier) => looksLikePhone(identifier) &&
  (identifier.match(/\d/g) || []).length <= 15;

const safeGet = (key) => {
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const safeSet = (key, value) => {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Private browsing can deny storage; sign-in still works.
  }
};

const greetingForNow = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const initialTheme = () => {
  const saved = safeGet(THEME_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
    ? "dark"
    : "light";
};

export default function CompanyLogin() {
  const dispatch = useDispatch();
  const redirect = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { accessType } = useSelector((state) => state.clientFunction || {});

  const userRef = useRef(null);
  const passwordRef = useRef(null);
  const loginInFlight = useRef(false);
  const focusPasswordInitially = useRef(Boolean(safeGet(REMEMBER_KEY)));

  const [clientLoginData, setClientLoginData] = useReducer(
    (current, update) => ({ ...current, ...update }),
    undefined,
    () => ({ user_name: safeGet(REMEMBER_KEY), stud_password: "" }),
  );
  const [rememberId, setRememberId] = useState(() => Boolean(safeGet(REMEMBER_KEY)));
  const [loginMethod, setLoginMethod] = useState(() =>
    looksLikePhone(safeGet(REMEMBER_KEY)) ? "phone" : "identity",
  );
  const [theme, setTheme] = useState(initialTheme);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [status, setStatus] = useState("idle");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);
  const isBusy = status !== "idle";

  useEffect(() => {
    (focusPasswordInitially.current ? passwordRef : userRef).current?.focus();
  }, []); // Initial focus only; never steal focus when the user types.

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return undefined;
    const original = meta.getAttribute("content");
    meta.setAttribute("content", "#065ad8");
    return () => {
      if (original === null) meta.removeAttribute("content");
      else meta.setAttribute("content", original);
    };
  }, []);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    safeSet(THEME_KEY, next);
  };

  const handleChange = (field) => (event) => {
    setClientLoginData({ [field]: event.target.value });
    setFieldErrors((previous) => ({ ...previous, [field]: "" }));
    setFormError("");
  };

  const changeLoginMethod = (nextMethod) => {
    if (isBusy || loginMethod === nextMethod) return;
    setLoginMethod(nextMethod);
    setClientLoginData({ user_name: "" });
    setFieldErrors({});
    setFormError("");
    userRef.current?.focus();
  };

  const showLoginError = (message) => {
    setFormError(message);
    enqueueSnackbar(message, { variant: "error", autoHideDuration: 4000 });
  };

  const executeLogin = async (event) => {
    event.preventDefault();
    if (loginInFlight.current) return;

    const username = clientLoginData.user_name?.trim() || "";
    const errors = {
      user_name: !username
        ? (loginMethod === "phone" ? "Enter your registered phone number." : "Enter your username or email address.")
        : loginMethod === "phone" && !isValidPhone(username)
          ? "Enter a valid phone number (7–15 digits)."
          : "",
      stud_password: clientLoginData.stud_password ? "" : "Enter your password.",
    };
    setFieldErrors(errors);
    setFormError("");

    if (errors.user_name) return userRef.current?.focus();
    if (errors.stud_password) return passwordRef.current?.focus();
    if (!online) return showLoginError("You're offline. Reconnect and try again.");

    loginInFlight.current = true;
    setStatus("loading");
    let succeeded = false;

    try {
      // Keep the existing owner/user API split and request payload exactly as used by the app.
      const data = { user_name: username, stud_password: clientLoginData.stud_password };
      const endpoint = username.includes("@universe.erp")
        ? `${serverHost}/client/users/staff_logins`
        : `${serverHost}/clients/logins`;
      const loginResponse = await axios.post(endpoint, data);
      const loginData = loginResponse.data;

      if (loginData?.status !== 200) {
        showLoginError(loginData?.message || "Unable to sign in. Check your details and try again.");
        passwordRef.current?.focus();
        return;
      }
      if (!loginData?.clientInfo) {
        showLoginError("The account response was incomplete. Please try again.");
        return;
      }

      const dbPayload = {
        clientData: loginData.clientInfo,
        staffData: loginData?.authenticationType === "USER_ACCESS" ? loginData.userData : null,
        accessFeatures: loginData?.accessFeatures,
      };
      dispatch(
        Action.startclientAction({
          dbPayload,
          authenticationType: loginData.authenticationType,
          sessionToken: loginData.sessionToken,
        }),
      );

      // Preserve the existing UniCloud event and its owner-only activation condition.
      const emitUniCloud =
        loginData.authenticationType === "CLIENT_ACCESS" &&
        loginData?.clientInfo?.uniCloud?.isUniCloudActive === true;
      if (emitUniCloud) {
        try {
          socket.emit("createUniCloudNotification", {
            clientId: loginData?.clientInfo?.clientId,
            uniCloud: loginData?.clientInfo?.uniCloud,
            payload: {
              userId: loginData?.clientInfo?.clientId,
              userName: loginData?.clientInfo?.founder?.first_name,
              userPhone: loginData?.clientInfo?.founder?.phone,
              userRole: loginData?.clientInfo?.founder?.role,
              device: "Android",
              loginMethod: "Password",
              tags: ["login", `${accessType === "USER_ACCESS" ? "Client" : "Staff"}`],
            },
            uniCloudPayload: {
              type: "login",
              heading: `New Login by ${accessType === "USER_ACCESS" ? "User" : `${loginData?.clientInfo?.founder?.first_name}`}`,
              description: "A user logged into the Universe Inventory account.",
            },
          });
        } catch (notificationError) {
          // An optional socket notification must not interrupt authenticated navigation.
          console.warn("UniCloud login notification was not emitted.");
        }
      }

      safeSet(REMEMBER_KEY, rememberId ? username : "");
      succeeded = true;
      setStatus("success");
      redirect(
        userHomePath(loginData.authenticationType, loginData.userData, loginData.clientInfo),
        { replace: true },
      );
    } catch (error) {
      const message = error?.code === "ERR_NETWORK"
        ? "No internet connection. Please check your network and try again."
        : error?.response?.data?.message || "Something went wrong. Please try again.";
      showLoginError(message);
      passwordRef.current?.focus();
    } finally {
      loginInFlight.current = false;
      if (!succeeded) setStatus("idle");
    }
  };

  return (
    <main className="universe-client-login" data-theme={theme}>
      <section className="ucl-showcase" aria-label="Universe Client Portal">
        <div className="ucl-orbit ucl-orbit-one" aria-hidden="true" />
        <div className="ucl-orbit ucl-orbit-two" aria-hidden="true" />
        <Link className="ucl-brand" to="/" aria-label="Universe Inventory home">
          <span className="ucl-brand-icon"><img src={UniLogo} alt="" /></span>
          <span className="ucl-brand-name">
            <strong>Universe Inventory</strong>
            <small>Management System</small>
          </span>
        </Link>

        <div className="ucl-showcase-body">
          <span className="ucl-eyebrow"><span className="ucl-status-dot" /> UNIVERSE CLIENT PORTAL</span>
          <h1>Your business.<br /><span>One connected workspace.</span></h1>
          <p>Manage inventory, sales, everyday operations and business growth from one secure platform.</p>
          <div className="ucl-feature-strip" aria-label="Client workspace features">
            <span>Inventory</span><span>Sales</span><span>Business insights</span>
          </div>
          <div className="ucl-showcase-accent" aria-hidden="true">
            <span /><span /><span /><span /><span /><span /><span />
          </div>
        </div>

        <footer className="ucl-showcase-footer">
          <div><ShieldOutlined fontSize="small" /> Secure access to your company workspace</div>
          <span>© {new Date().getFullYear()} Universe Inventory</span>
        </footer>
      </section>

      <section className="ucl-auth-panel" aria-label="Client sign-in form">
        <button
          className="ucl-theme-toggle"
          type="button"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          title={theme === "dark" ? "Light appearance" : "Dark appearance"}
        >
          {theme === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}
        </button>

        <div className="ucl-form-wrapper">
          <header className="ucl-form-header">
            <span className="ucl-auth-eyebrow">UNIVERSE CLIENT ACCESS</span>
            <h2>{greetingForNow()}, welcome back.</h2>
            <p>Sign in to your business or authorized user account.</p>
          </header>

          {!online && <div className="ucl-alert ucl-alert-warning" role="status">
            <WifiOffRounded /><span>You're offline. Reconnect to sign in.</span>
          </div>}
          {formError && <div className="ucl-alert" role="alert">
            <ErrorOutlineRounded /><span>{formError}</span>
          </div>}

          <form className="ucl-form" onSubmit={executeLogin} noValidate>
            <div className="ucl-login-methods" role="group" aria-label="Choose sign-in method">
              <button type="button" className={`ucl-login-method ${loginMethod === "identity" ? "is-active" : ""}`}
                onClick={() => changeLoginMethod("identity")} aria-pressed={loginMethod === "identity"} disabled={isBusy}>
                <PersonRounded fontSize="small" /> Email / Username
              </button>
              <button type="button" className={`ucl-login-method ${loginMethod === "phone" ? "is-active" : ""}`}
                onClick={() => changeLoginMethod("phone")} aria-pressed={loginMethod === "phone"} disabled={isBusy}>
                <PhoneRounded fontSize="small" /> Phone number
              </button>
            </div>
            <div className="ucl-field">
              <label htmlFor="ucl-username">{loginMethod === "phone" ? "Registered phone number" : "Username or email"}</label>
              <div className={`ucl-input-wrap ${fieldErrors.user_name ? "ucl-invalid" : ""}`}>
                {loginMethod === "phone" ? <PhoneRounded aria-hidden="true" /> : <PersonRounded aria-hidden="true" />}
                <input
                  ref={userRef}
                  id="ucl-username"
                  name="user_name"
                  type={loginMethod === "phone" ? "tel" : "text"}
                  inputMode={loginMethod === "phone" ? "tel" : "email"}
                  autoComplete={loginMethod === "phone" ? "tel" : "username"}
                  autoCapitalize="none"
                  spellCheck="false"
                  placeholder={loginMethod === "phone" ? "e.g. +2348012345678" : "Enter username or email"}
                  value={clientLoginData.user_name}
                  onChange={handleChange("user_name")}
                  aria-invalid={Boolean(fieldErrors.user_name)}
                  aria-describedby={fieldErrors.user_name ? "ucl-username-error" : undefined}
                  disabled={isBusy}
                />
              </div>
              {fieldErrors.user_name && <small className="ucl-field-error" id="ucl-username-error">{fieldErrors.user_name}</small>}
              {loginMethod === "phone" && <p className="ucl-login-hint">For company account owners. Use your phone number exactly as registered, including the country code if supplied. Company staff should sign in with their issued username.</p>}
            </div>

            <div className="ucl-field">
              <div className="ucl-password-heading">
                <label htmlFor="ucl-password">Password</label>
                <button type="button" className="ucl-text-button" onClick={() => setSupportOpen((open) => !open)} aria-expanded={supportOpen} aria-controls="ucl-recovery-help">
                  Forgot password?
                </button>
              </div>
              {supportOpen && <div className="ucl-recovery" id="ucl-recovery-help" role="status">
                Need help accessing your account? <Link to="/contact-us">Contact Universe support</Link> for assistance.
              </div>}
              <div className={`ucl-input-wrap ${fieldErrors.stud_password ? "ucl-invalid" : ""}`}>
                <LockRounded aria-hidden="true" />
                <input
                  ref={passwordRef}
                  id="ucl-password"
                  name="stud_password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={clientLoginData.stud_password}
                  onChange={handleChange("stud_password")}
                  onKeyUp={(event) => setCapsLockOn(Boolean(event.getModifierState?.("CapsLock")))}
                  onKeyDown={(event) => setCapsLockOn(Boolean(event.getModifierState?.("CapsLock")))}
                  aria-invalid={Boolean(fieldErrors.stud_password)}
                  aria-describedby={fieldErrors.stud_password ? "ucl-password-error" : undefined}
                  disabled={isBusy}
                />
                <button
                  type="button"
                  className="ucl-password-toggle"
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  disabled={isBusy}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
              {fieldErrors.stud_password && <small className="ucl-field-error" id="ucl-password-error">{fieldErrors.stud_password}</small>}
              {capsLockOn && <small className="ucl-caps-notice"><KeyboardCapslockRounded fontSize="small" /> Caps Lock is on</small>}
            </div>

            <label className="ucl-remember">
              <input
                type="checkbox"
                checked={rememberId}
                onChange={(event) => setRememberId(event.target.checked)}
                disabled={isBusy}
              />
              <span>Remember my {loginMethod === "phone" ? "phone number" : "username"} on this device</span>
            </label>

            <button className="ucl-submit" type="submit" disabled={isBusy || !online}>
              {status === "loading" ? <><span className="ucl-spinner" aria-hidden="true" /> Signing in...</>
                : status === "success" ? <><CheckCircleOutlineRounded /> Signed in</>
                : <>Sign in to your workspace <ArrowForwardRounded /></>}
            </button>
          </form>

          <div className="ucl-form-footer">
            <p>New to Universe? <Link to="/create_new_account">Create an account</Link></p>
            <span><ShieldOutlined fontSize="small" /> Your account access follows your assigned permissions.</span>
          </div>
        </div>
      </section>
    </main>
  );
}
